import { useCallback, useMemo } from 'react';
import { useSearchParams, type NavigateOptions } from 'react-router';

type ParamType = 'scalar' | 'array';

type ParamsSchema = Record<string, ParamType>;
type ParamsKey<S extends ParamsSchema> = Extract<keyof S, string>;

type ParamsResult<S extends ParamsSchema> = {
  [P in keyof S]?: S[P] extends 'array' ? string[] : string;
};

type ParamsUpdate<S extends ParamsSchema> = Partial<{
  [P in keyof S]: S[P] extends 'array' ? string[] | undefined : string | undefined;
}>;

/**
 * {@link useUrlParams} is a react hook to assist when working with multiple search params in the url.
 *
 * Give it a schema of param names and types that you want to get from the current url, and you'll get an
 * object with those params as keys and their values as strings/string arrays. You'll also get an updater
 * function, which can be used
 * to update the params in the url.  Pass it an object with the keys/values you want in the url.
 *
 * For params that can appear multiple times (e.g. `?tag=a&tag=b`), mark them as `'array'`.
 * They will be returned as `string[]` and the setter will accept `string[]` values.
 *
 * Note: This does no stringifying or encoding/decoding, so only pass it string values! (or `undefined` to unset)
 */
export function useUrlParams<const S extends ParamsSchema>(paramSchema: S) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filteredParams = useMemo(() => {
    const result: Record<string, string | string[]> = {};
    for (const [name, type] of Object.entries(paramSchema) as Array<[ParamsKey<S>, S[ParamsKey<S>]]>) {
      if (type === 'array') {
        const values = searchParams.getAll(name);
        if (values.length > 0) {
          result[name] = values;
        }
      } else {
        const value = searchParams.get(name);
        if (value !== null) {
          result[name] = value;
        }
      }
    }
    return result as ParamsResult<S>;
  }, [paramSchema, searchParams]);

  const setParams = useCallback(
    (values: ParamsUpdate<S>, navigateOpts?: NavigateOptions) => {
      const newSearchParams = new URLSearchParams();

      // Preserve all existing params, overriding only the ones specified in `values`
      for (const [key, value] of searchParams) {
        // Skip keys that are being explicitly set — we'll handle those below
        if (key in (values as Record<string, unknown>)) {
          continue;
        }
        newSearchParams.append(key, value);
      }

      // Apply the new values
      for (const key of Object.keys(values) as Array<ParamsKey<S>>) {
        const value = values[key];
        if (value === undefined) {
          // undefined = remove the param
          continue;
        }
        if (Array.isArray(value)) {
          for (const v of value) {
            newSearchParams.append(key, v);
          }
        } else {
          newSearchParams.append(key, value);
        }
      }

      setSearchParams(newSearchParams, navigateOpts);
    },
    [searchParams, setSearchParams]
  );

  return [filteredParams, setParams] as const;
}
